import React, { useEffect, useMemo } from "react";
import "./style.css";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  HiOutlineTruck,
  HiOutlineClipboardList,
  HiOutlineUser,
} from "react-icons/hi";
import Steps from "./Steps";
import Greatings from "./Greatings";
import CarDetailForm from "../../components/CarDetailForm/index.js";
import { SELL_MY_CAR_STEPS } from "../../constant";
import { sellMyCarStepsName } from "../../constant/constants";
import RegPlate from "../../components/RegPlate";
import { useContext } from "react";
import FeedbackContext from "../../context/cmscontext";
import { scrollTop } from "../../utils/window";
import { normalizeChoiceField } from "../../utils/sellMyCarFields";
import { resolveEvaluationPlateCountry } from "../../utils/plateCountry";

const HERO_IMAGE = "/images/hero-evaluation.jpg";

const FLOW_STEPS = [
  { id: 1, label: "Your vehicle", Icon: HiOutlineTruck },
  { id: 2, label: "Car details", Icon: HiOutlineClipboardList },
  { id: 3, label: "Get valuation", Icon: HiOutlineUser },
];

function Evaluation(props) {
  const { feedback } = useContext(FeedbackContext);
  const evaluation_page = feedback?.evaluation_page || {};
  const {
    step1,
    step2,
    currentStep,
    setcurrentStep,
    reg_number,
    setplate_country,
  } = props;

  const history = useHistory();
  const location = useLocation();

  const regRaw = location.state?.Car_Reg_N ?? reg_number ?? "";
  const flowReg =
    regRaw == null || regRaw === "" || typeof regRaw === "object"
      ? ""
      : String(regRaw).trim().toUpperCase();

  useEffect(() => {
    const fromNav = location.state?.plateCountry;
    if (fromNav && typeof setplate_country === "function") {
      setplate_country(fromNav);
    }
  }, [location.state?.plateCountry, setplate_country]);

  const plateCountry = resolveEvaluationPlateCountry({
    plateCountry: step2?.plate_country || location.state?.plateCountry,
    reg: flowReg,
  });

  const vehicleReady = Boolean(
    step1?.variant?.isSelected &&
    step1?.brand?.isSelected &&
    (step1?.variant?.current?.value || step1?.variant?.current?.display) &&
    (step1?.brand?.current?.id || step1?.brand?.current?.brand_name)
  );
  const inValuationFlow = Boolean(flowReg);
  const showVehicleStep = inValuationFlow && !vehicleReady;
  const activeUiStep = showVehicleStep ? 1 : Math.min(Math.max(currentStep, 2), 3);

  const s2lcItems = [
    evaluation_page.What_happens_text1,
    evaluation_page.What_happens_text2,
    evaluation_page.What_happens_text3,
    evaluation_page.What_happens_text4,
    evaluation_page.What_happens_text5,
  ]
    .map((text) => (text || "").trim())
    .filter(Boolean);

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    if (!inValuationFlow && !vehicleReady) {
      history.replace("/");
    }
  }, [history, inValuationFlow, vehicleReady]);

  const vehicleSummary = useMemo(() => {
    if (!vehicleReady) return null;
    return {
      brand: step1?.brand?.current?.brand_name,
      brandImage: step1?.brand?.current?.brand_image,
      model: step1?.model?.current?.display,
      fuel: step1?.fuel_type?.current?.display,
      year: step1?.year?.current?.display,
      variant: step1?.variant?.current?.display,
      color: step1?.brand?.current?.color,
    };
  }, [step1, vehicleReady]);

  const vehicleExtras = useMemo(() => {
    const mileage = normalizeChoiceField(step2?.mileage);
    if (!mileage.isSelected) return null;
    return {
      mileage: mileage.current.title,
      owners: normalizeChoiceField(step2?.previous_owners).current.title,
      serviceHistory: normalizeChoiceField(step2?.services_history).current.title,
    };
  }, [step2]);

  if (!inValuationFlow && !vehicleReady) {
    return null;
  }

  if (!showVehicleStep && currentStep > sellMyCarStepsName.length - 2) {
    return (
      <div className="pi-evaluation">
        <Greatings />
      </div>
    );
  }

  return (
    <div className="pi-evaluation">
      <header className="pi-inner-hero">
        <div className="pi-inner-hero__media" aria-hidden>
          <img
            className="pi-inner-hero__media-img"
            src={`${process.env.PUBLIC_URL}${HERO_IMAGE}`}
            alt=""
          />
          <div className="pi-inner-hero__media-shade" />
        </div>
        <div className="pi-inner-hero__fx" aria-hidden>
          <div className="pi-inner-hero__glow pi-inner-hero__glow--a" />
          <div className="pi-inner-hero__glow pi-inner-hero__glow--b" />
          <div className="pi-inner-hero__grid" />
        </div>
        <div className="pi-inner-hero__content">
          <div className="pi-inner-hero__copy">
            <p className="pi-inner-hero__label">Vehicle valuation</p>
            <h1 className="pi-inner-hero__title">
              {showVehicleStep ? "Confirm your vehicle" : "Complete your valuation"}
            </h1>
            <p className="pi-inner-hero__lead">
              {showVehicleStep
                ? "We could not auto-match every detail from your plate. Select your vehicle below to continue."
                : "A few quick details and we will prepare your market valuation."}
            </p>
          </div>
        </div>
      </header>

      <section className="pi-evaluation__body">
        <div className="pi-evaluation__inner">
          <nav className="pi-evaluation__steps" aria-label="Valuation progress">
            {FLOW_STEPS.map(({ id, label, Icon }) => (
              <div
                key={id}
                className={`pi-evaluation__step ${id === activeUiStep ? "is-active" : ""} ${
                  id < activeUiStep ? "is-complete" : ""
                }`}
              >
                <span className="pi-evaluation__step-icon" aria-hidden>
                  <Icon size={18} />
                </span>
                <span className="pi-evaluation__step-label">{label}</span>
              </div>
            ))}
          </nav>

          {showVehicleStep ? (
            <div className="pi-evaluation__stage pi-evaluation__stage--single">
              <div className="pi-evaluation__panel">
                <div className="pi-evaluation__panel-head">
                  <p className="pi-evaluation__panel-eyebrow">Step 1 of 3</p>
                  <h2>Select your vehicle</h2>
                  {flowReg ? (
                    <RegPlate
                      value={flowReg}
                      country={plateCountry}
                      className="pi-evaluation__reg"
                    />
                  ) : null}
                  <p className="pi-evaluation__panel-lead">
                    Choose brand, model, fuel type, year, and variant to continue.
                  </p>
                </div>
                <div className="pi-evaluation__form-shell">
                  <CarDetailForm
                    reducer="sellMyCar"
                    reduxType={SELL_MY_CAR_STEPS}
                    title=""
                    formWidth="fluid"
                    childClassName="pi-evaluation__fields pi-evaluation__fields--step1"
                    buttonClassName="pi-evaluation__submit-btn"
                    onSubmit={
                      step1?.variant?.isSelected &&
                      (step1?.variant?.current?.value || step1?.variant?.current?.display)
                        ? () => {
                            setcurrentStep(2);
                            scrollTop();
                          }
                        : () => {}
                    }
                    buttonText="Continue"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="pi-evaluation__stage">
              <aside className="pi-evaluation__summary">
                <div className="pi-evaluation__summary-card">
                  <p className="pi-evaluation__panel-eyebrow">
                    Step {activeUiStep} of 3
                  </p>
                  <h2>Your vehicle</h2>

                  {vehicleSummary?.brandImage ? (
                    <img
                      className="pi-evaluation__summary-image"
                      src={vehicleSummary.brandImage}
                      alt=""
                    />
                  ) : null}

                  {flowReg ? (
                    <RegPlate
                      value={flowReg}
                      country={plateCountry}
                      className="pi-evaluation__reg"
                    />
                  ) : null}

                  <p className="pi-evaluation__summary-title">
                    {vehicleSummary?.brand} · {vehicleSummary?.variant}
                  </p>
                  <p className="pi-evaluation__summary-meta">
                    {[
                      vehicleSummary?.model,
                      vehicleSummary?.color,
                      vehicleSummary?.fuel,
                      vehicleSummary?.year,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>

                  {currentStep > 2 && vehicleExtras ? (
                    <div className="pi-evaluation__summary-extra">
                      <span>{vehicleExtras.mileage} km</span>
                      <span>{vehicleExtras.owners} owners</span>
                      <span>{vehicleExtras.serviceHistory} service history</span>
                    </div>
                  ) : null}
                </div>

                {currentStep === 2 && s2lcItems.length ? (
                  <div className="pi-evaluation__info-card">
                    <h3>{(evaluation_page.What_happens_inspection || "").trim()}</h3>
                    <ul className="pi-evaluation__info-list">
                      {s2lcItems.map((text, index) => (
                        <li key={`${index}-${text.slice(0, 24)}`}>
                          <span className="pi-evaluation__info-num" aria-hidden>
                            {index + 1}
                          </span>
                          <span className="pi-evaluation__info-text">{text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </aside>

              <div className="pi-evaluation__panel">
                <div className="pi-evaluation__panel-head pi-evaluation__panel-head--form">
                  <p className="pi-evaluation__panel-eyebrow">
                    Step {activeUiStep} of 3
                  </p>
                  <h2>
                    {currentStep === 2
                      ? "Additional car details"
                      : "Your contact details"}
                  </h2>
                  <p className="pi-evaluation__panel-lead">
                    {currentStep === 2
                      ? "Share mileage, ownership and service history for an accurate valuation."
                      : "We will send your valuation to these contact details."}
                  </p>
                </div>
                <div className="pi-evaluation__form-shell">
                  <Steps currentStep={currentStep} setcurrentStep={setcurrentStep} />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const mapStateToProps = (state) => ({
  reg_number: state.sellMyCar.step2.reg_number,
  step1: state.sellMyCar.step1,
  step2: state.sellMyCar.step2,
  currentStep: state.sellMyCar.currentStep,
});

const mapDispatchToProps = (dispatch) => ({
  setcurrentStep: (data) =>
    dispatch({
      type: SELL_MY_CAR_STEPS,
      payload: {
        type: "current_step",
        data,
      },
    }),
  setplate_country: (data) =>
    dispatch({
      type: SELL_MY_CAR_STEPS,
      payload: {
        type: "plate_country",
        data,
      },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Evaluation);
